/** @file Local setup transform that puts a bone at a given world matrix under a given parent world. */
import { affine, type Affine } from '@pose2d/math/affine';
import { throwError } from '@pose2d/format/throwError';
import type { LocalTransform } from '@pose2d/format/types';
import { snapSetup } from './snapSetup';

/** `decompose(parent⁻¹ × world)` in normal form (shearX 0, reflection as negative scaleY); VALIDATION at `path` for a singular parent. */
export function localFromWorld(parentWorld: Affine, world: Affine, path: string): LocalTransform {
  const inverse = [0, 0, 0, 0, 0, 0];
  affine.invert(inverse, parentWorld);
  if (!inverse.every(Number.isFinite)) {
    throwError('VALIDATION', undefined, { path, hint: 'the parent has a zero setup scale, so no local transform can reproduce this world pose' });
  }
  const local = [0, 0, 0, 0, 0, 0];
  const t = [0, 0, 0, 0, 0, 0, 0];
  affine.decompose(affine.mul(local, inverse, world), t);
  const at = (i: number): number => snapSetup(t[i] ?? 0);
  return { x: at(0), y: at(1), rotation: at(2), scaleX: at(3), scaleY: at(4), shearX: at(5), shearY: at(6) };
}
